import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { SupportService } from '@/app/services/support/support.service';
import { BriefApplicationService } from '@/app/services/brief-application/brief-application.service';
import { BriefService } from '@/app/services/brief/brief.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { MiscService } from '@/app/services/misc/misc.service';
import { ImageService } from '@/app/services/image/image.service';
import { FormatService } from '@/app/common/format/format.service';
import {
  SupportAdminOption,
  SupportAlertPreferences,
  SupportAttachment,
  SupportMessage,
  SupportTemplateMessage,
  SupportAiAction,
  SupportAiSuggestion,
  SupportTicketDetail,
  SupportTikTokConnection,
  SupportUserType,
} from '@/app/common/general-types/support-ticket';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
  BriefResponseDto,
  BriefStatusEnum,
} from '@/app/common/general-types/briefs.dto';
import { supportTicketCategoryLabel } from '@/app/common/support-ticket-category.util';
import {
  formatSupportAdminDateTime,
  formatSupportDurationMs,
} from '@/app/common/support-date.util';
import { SUPPORT_ADMIN_ROLES } from '@/app/common/support-admin-roles.util';
import { applySupportTemplateMessage } from '@/app/common/support-template-message.util';
import {
  formatSupportCountry,
  supportCountryFlagForTicket,
} from '@/app/common/support-region.util';
import { SupportAttachmentInputComponent } from '@/app/molecules/support-attachment-input/support-attachment-input.component';
import { SupportAttachmentsDisplayComponent } from '@/app/molecules/support-attachments-display/support-attachments-display.component';

@Component({
  selector: 'app-support-admin-ticket',
  imports: [
    FormsModule,
    RouterLink,
    SupportAttachmentInputComponent,
    SupportAttachmentsDisplayComponent,
  ],
  templateUrl: './support-admin-ticket.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportAdminTicketComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly supportService = inject(SupportService);
  private readonly briefApplicationService = inject(BriefApplicationService);
  private readonly briefService = inject(BriefService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly miscService = inject(MiscService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly formatService = inject(FormatService);
  readonly imageService = inject(ImageService);

  private readonly pollIntervalMs = 8000;
  /** Short fixed delay for a ticket's very first admin response, mirrors the backend's
   * first-response notification delay so the pending-notify UI matches actual timing. */
  private readonly firstResponseNotifyDelayMs = 10 * 1000;
  /** How long to keep showing "Sent!" for a ticket's first response once its countdown
   * reaches zero, before the pending block disappears - independent of when the next poll
   * actually confirms notificationsSent, so the confirmation is never a 0ms flash. */
  private readonly firstResponseSentFlashMs = 3000;
  private loadedCreatorBriefsForUserId: string | null = null;
  private loadedBrandBriefsForAccountId: string | null = null;
  private userIdCopiedResetHandle: ReturnType<typeof setTimeout> | null = null;
  private emailCopiedResetHandle: ReturnType<typeof setTimeout> | null = null;
  private pendingScrollBehavior: ScrollBehavior | null = null;

  subscriptionList = new Subscription();

  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLElement>;
  @ViewChild('messagesEnd') private messagesEnd?: ElementRef<HTMLElement>;

  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly error = signal('');
  readonly ticket = signal<SupportTicketDetail | null>(null);
  readonly nowMs = signal(Date.now());
  readonly notifyDelayMs = signal(2 * 60 * 1000);
  readonly skipDelayForFirstResponse = signal(true);
  readonly timingExpanded = signal(false);
  readonly templatesMenuOpen = signal(false);
  readonly userIdCopied = signal(false);
  readonly emailCopied = signal(false);
  readonly hiredBriefs = signal<BriefApplicationResponseDto[]>([]);
  readonly hiredBriefsLoading = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly hiredBriefsExpanded = signal(false);
  readonly appliedBriefs = signal<BriefApplicationResponseDto[]>([]);
  readonly appliedBriefsLoading = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly appliedBriefsExpanded = signal(false);
  readonly brandBriefs = signal<BriefResponseDto[]>([]);
  readonly brandBriefsLoading = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly aiSuggestion = signal<SupportAiSuggestion | null>(null);
  readonly aiLoading = signal(false);
  readonly aiError = signal('');
  readonly approvingActionId = signal<string | null>(null);
  readonly tiktokConnection = signal<SupportTikTokConnection | null>(null);
  readonly tiktokLoading = signal(false);
  readonly tiktokDisconnecting = signal(false);
  readonly tiktokError = signal('');

  private readonly creatorBriefPreviewCount = 3;

  readonly visibleAppliedBriefs = computed(() => {
    const briefs = this.appliedBriefs();
    return this.appliedBriefsExpanded() ? briefs : briefs.slice(0, this.creatorBriefPreviewCount);
  });

  readonly visibleHiredBriefs = computed(() => {
    const briefs = this.hiredBriefs();
    return this.hiredBriefsExpanded() ? briefs : briefs.slice(0, this.creatorBriefPreviewCount);
  });

  readonly showMoreAppliedBriefs = computed(
    () => !this.appliedBriefsExpanded() && this.appliedBriefs().length > this.creatorBriefPreviewCount,
  );

  readonly showMoreHiredBriefs = computed(
    () => !this.hiredBriefsExpanded() && this.hiredBriefs().length > this.creatorBriefPreviewCount,
  );

  reply = '';
  attachments: SupportAttachment[] = [];
  admins: SupportAdminOption[] = [];
  templateMessages: SupportTemplateMessage[] = [];
  selectedAdminId: string | null = null;
  ticketId = '';
  currentAdminUserId = '';
  /** Ordered ticket IDs from the queue the admin came from, so the prev/next arrows can
   * flick between tickets without going back to the list. Empty when a ticket is opened
   * directly (e.g. a deep link or page refresh). */
  ticketOrder: string[] = [];
  editingMessageId: string | null = null;
  editingBody = '';
  editingAttachments: SupportAttachment[] = [];

  readonly formatDateTime = formatSupportAdminDateTime;
  readonly formatDuration = formatSupportDurationMs;
  readonly countryFlagForTicket = supportCountryFlagForTicket;
  readonly formatCountry = formatSupportCountry;
  readonly chatViewportHeight = 'calc(100vh - 4rem)';

  ngOnInit(): void {
    if (!this.currentUserService.hasAnyRole(SUPPORT_ADMIN_ROLES)) {
      this.router.navigate(['/']);
      return;
    }

    this.currentAdminUserId = this.currentUserService.shoutUser?.firebaseUid ?? '';

    const navigationState = history.state as { ticketIds?: string[] } | null;
    this.ticketOrder = navigationState?.ticketIds ?? [];

    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => {
        this.ticketId = params.get('ticketId') ?? params.get('id') ?? '';
        this.aiSuggestion.set(null);
        this.aiError.set('');
        this.loadTicket();
        this.loadAdmins();
        this.loadTemplateMessages();
      }),
    );

    this.subscriptionList.add(
      interval(this.pollIntervalMs).subscribe(() => this.pollTicket()),
    );

    this.subscriptionList.add(
      interval(1000).subscribe(() => {
        this.nowMs.set(Date.now());
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.userIdCopiedResetHandle !== null) {
      clearTimeout(this.userIdCopiedResetHandle);
    }
    if (this.emailCopiedResetHandle !== null) {
      clearTimeout(this.emailCopiedResetHandle);
    }
    this.subscriptionList.unsubscribe();
  }

  ngAfterViewChecked(): void {
    this.flushScrollToLatest();
  }

  private pollTicket(): void {
    if (
      document.visibilityState !== 'visible' ||
      this.submitting() ||
      this.editingMessageId ||
      !this.ticketId
    ) {
      return;
    }

    this.loadTicket({ silent: true });
  }

  loadTicket(options?: { silent?: boolean }): void {
    if (!this.ticketId) {
      return;
    }

    if (!options?.silent) {
      this.loading.set(true);
      this.error.set('');
      this.userIdCopied.set(false);
      this.emailCopied.set(false);
      this.templatesMenuOpen.set(false);
      this.clearHiredBriefs();
      this.clearBrandBriefs();
      this.tiktokConnection.set(null);
      this.tiktokError.set('');
    }

    this.subscriptionList.add(
      this.supportService.getAdminTicket(this.ticketId).subscribe({
        next: (ticket: SupportTicketDetail) => {
          const previousCount = this.ticket()?.messages?.length ?? 0;
          const nextCount = ticket.messages?.length ?? 0;
          this.ticket.set(ticket);
          if (!this.editingMessageId) {
            this.selectedAdminId = ticket.assignedAdminUserId;
          }
          this.maybeLoadContextBriefs(ticket);
          if (!options?.silent) {
            this.maybeLoadTikTokConnection(ticket);
            this.loading.set(false);
            this.queueScrollToLatest('auto');
            this.maybeRequestAiDraft(ticket);
          } else if (nextCount > previousCount && this.isNearBottom()) {
            this.queueScrollToLatest('smooth');
          }
          this.cdr.markForCheck();
        },
        error: () => {
          if (!options?.silent) {
            this.ticket.set(null);
            this.clearHiredBriefs();
            this.clearBrandBriefs();
            this.tiktokConnection.set(null);
            this.tiktokError.set('');
            this.loading.set(false);
            this.error.set('Ticket not found.');
            this.cdr.markForCheck();
          }
        },
      }),
    );
  }

  private queueScrollToLatest(behavior: ScrollBehavior = 'auto'): void {
    this.pendingScrollBehavior = behavior;
  }

  private flushScrollToLatest(): void {
    const behavior = this.pendingScrollBehavior;
    if (!behavior) {
      return;
    }
    this.pendingScrollBehavior = null;

    const end = this.messagesEnd?.nativeElement;
    const container = this.messagesContainer?.nativeElement;
    if (!end || !container) {
      return;
    }

    end.scrollIntoView({ behavior, block: 'end' });
    if (behavior === 'auto') {
      container.scrollTop = container.scrollHeight;
    }
  }

  private isNearBottom(): boolean {
    const container = this.messagesContainer?.nativeElement;
    if (!container) {
      return true;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom < 120;
  }

  toggleTiming(): void {
    this.timingExpanded.update((open) => !open);
  }

  toggleTemplatesMenu(): void {
    this.templatesMenuOpen.update((open) => !open);
  }

  closeTemplatesMenu(): void {
    this.templatesMenuOpen.set(false);
  }

  copyUserId(userId: string): void {
    this.copyField(userId, 'User ID', this.userIdCopied, 'userId');
  }

  copyEmail(email: string): void {
    this.copyField(email, 'Email', this.emailCopied, 'email');
  }

  private copyField(
    value: string,
    label: string,
    copiedSignal: typeof this.userIdCopied,
    field: 'userId' | 'email',
  ): void {
    if (!value) {
      return;
    }

    this.miscService.copyToClipboard(value, label);
    copiedSignal.set(true);
    this.cdr.markForCheck();

    const handle =
      field === 'userId' ? this.userIdCopiedResetHandle : this.emailCopiedResetHandle;
    if (handle !== null) {
      clearTimeout(handle);
    }

    const resetHandle = setTimeout(() => {
      copiedSignal.set(false);
      if (field === 'userId') {
        this.userIdCopiedResetHandle = null;
      } else {
        this.emailCopiedResetHandle = null;
      }
      this.cdr.markForCheck();
    }, 2000);

    if (field === 'userId') {
      this.userIdCopiedResetHandle = resetHandle;
    } else {
      this.emailCopiedResetHandle = resetHandle;
    }
  }

  hiredBriefTitle(application: BriefApplicationResponseDto): string {
    return application.brief?.title ?? 'Untitled brief';
  }

  hiredBriefSubtitle(application: BriefApplicationResponseDto): string {
    const campaign = application.brief?.campaign?.campaignName;
    const brand =
      application.brief?.businessAccount?.companyName ??
      application.businessAccount?.companyName;
    return [campaign, brand].filter(Boolean).join(' · ') || '—';
  }

  applicationBrandLogo(application: BriefApplicationResponseDto): string | null {
    return (
      application.brief?.businessAccount?.companyLogo ??
      application.businessAccount?.companyLogo ??
      null
    );
  }

  applicationStatusLabel(status: ApplicationStatusEnum): string {
    switch (status) {
      case ApplicationStatusEnum.Applied:
        return 'Applied';
      case ApplicationStatusEnum.Invited:
        return 'Invited';
      case ApplicationStatusEnum.Hired:
        return 'Hired';
      case ApplicationStatusEnum.ReadyToPost:
        return 'Ready to post';
      case ApplicationStatusEnum.Posted:
        return 'Posted';
      case ApplicationStatusEnum.Started:
        return 'Started';
      case ApplicationStatusEnum.Completed:
        return 'Completed';
      case ApplicationStatusEnum.Paid:
        return 'Paid';
      case ApplicationStatusEnum.BonusPaid:
        return 'Bonus paid';
      default:
        return 'Active';
    }
  }

  brandBriefTitle(brief: BriefResponseDto): string {
    return brief.title ?? 'Untitled brief';
  }

  brandBriefSubtitle(brief: BriefResponseDto): string {
    const campaign = brief.campaign?.campaignName;
    return campaign || this.formatDateTime(brief.createdDate) || '—';
  }

  expandAppliedBriefs(): void {
    this.appliedBriefsExpanded.set(true);
  }

  expandHiredBriefs(): void {
    this.hiredBriefsExpanded.set(true);
  }

  briefStatusLabel(brief: BriefResponseDto): string {
    switch (brief.currentStatus?.status) {
      case BriefStatusEnum.Public:
        return 'Public';
      case BriefStatusEnum.Private:
        return 'Private';
      case BriefStatusEnum.Complete:
        return 'Complete';
      case BriefStatusEnum.Draft:
        return 'Draft';
      case BriefStatusEnum.Deleted:
        return 'Deleted';
      default:
        return '—';
    }
  }

  private maybeLoadContextBriefs(ticket: SupportTicketDetail): void {
    const userType = ticket.user?.userType;

    if (userType === 'creator') {
      this.clearBrandBriefs();
      this.maybeLoadHiredBriefs(ticket);
      return;
    }

    if (userType === 'brand') {
      this.clearHiredBriefs();
      this.maybeLoadBrandBriefs(ticket);
      return;
    }

    this.clearHiredBriefs();
    this.clearBrandBriefs();
  }

  private maybeLoadTikTokConnection(ticket: SupportTicketDetail): void {
    if (ticket.user?.userType !== 'creator') {
      this.tiktokConnection.set(null);
      this.tiktokError.set('');
      return;
    }

    this.tiktokLoading.set(true);
    this.tiktokError.set('');
    this.subscriptionList.add(
      this.supportService.getTikTokConnection(ticket.id).subscribe({
        next: (connection) => {
          this.tiktokConnection.set(connection);
          this.tiktokLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.tiktokConnection.set(null);
          this.tiktokLoading.set(false);
          this.tiktokError.set('Could not load TikTok connection.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  disconnectTikTok(): void {
    const ticket = this.ticket();
    if (!ticket || this.tiktokDisconnecting()) {
      return;
    }

    const handle = this.tiktokConnection()?.handle || ticket.user?.tikTokHandle || 'this account';
    if (!confirm(`Disconnect TikTok for ${handle}? They can connect a replacement afterwards.`)) {
      return;
    }

    this.tiktokDisconnecting.set(true);
    this.tiktokError.set('');
    this.subscriptionList.add(
      this.supportService.disconnectTikTok(ticket.id).subscribe({
        next: (result) => {
          this.tiktokConnection.set(result.connection);
          this.tiktokDisconnecting.set(false);
          if (result.warning) {
            this.tiktokError.set(result.warning);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.tiktokDisconnecting.set(false);
          this.tiktokError.set('Could not disconnect TikTok.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private maybeLoadHiredBriefs(ticket: SupportTicketDetail): void {
    const userId = ticket.user?.userId;

    if (!userId) {
      this.clearHiredBriefs();
      return;
    }

    if (this.loadedCreatorBriefsForUserId === userId) {
      return;
    }

    this.loadCreatorBriefs(userId);
  }

  private maybeLoadBrandBriefs(ticket: SupportTicketDetail): void {
    const businessAccountId = ticket.businessAccountId;

    if (!businessAccountId) {
      this.brandBriefs.set([]);
      this.brandBriefsLoading.set('loaded');
      this.loadedBrandBriefsForAccountId = null;
      return;
    }

    if (this.loadedBrandBriefsForAccountId === businessAccountId) {
      return;
    }

    this.loadBrandBriefs(businessAccountId);
  }

  private loadCreatorBriefs(creatorUserId: string): void {
    this.loadedCreatorBriefsForUserId = creatorUserId;
    this.hiredBriefsLoading.set('loading');
    this.appliedBriefsLoading.set('loading');
    this.hiredBriefsExpanded.set(false);
    this.appliedBriefsExpanded.set(false);
    this.hiredBriefs.set([]);
    this.appliedBriefs.set([]);

    this.subscriptionList.add(
      this.briefApplicationService.filterCreatorsApplications(creatorUserId, 'hired', 10, 0).subscribe({
        next: (applications) => {
          if (this.loadedCreatorBriefsForUserId !== creatorUserId) {
            return;
          }
          this.hiredBriefs.set(applications);
          this.hiredBriefsLoading.set('loaded');
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.loadedCreatorBriefsForUserId !== creatorUserId) {
            return;
          }
          this.hiredBriefs.set([]);
          this.hiredBriefsLoading.set('error');
          this.cdr.markForCheck();
        },
      }),
    );

    this.subscriptionList.add(
      this.briefApplicationService.filterCreatorsApplications(creatorUserId, 'applied', 10, 0).subscribe({
        next: (applications) => {
          if (this.loadedCreatorBriefsForUserId !== creatorUserId) {
            return;
          }
          this.appliedBriefs.set(applications);
          this.appliedBriefsLoading.set('loaded');
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.loadedCreatorBriefsForUserId !== creatorUserId) {
            return;
          }
          this.appliedBriefs.set([]);
          this.appliedBriefsLoading.set('error');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private loadBrandBriefs(businessAccountId: string): void {
    this.loadedBrandBriefsForAccountId = businessAccountId;
    this.brandBriefsLoading.set('loading');
    this.brandBriefs.set([]);

    this.subscriptionList.add(
      this.briefService
        .getBriefsForBusinessAccount(businessAccountId, {
          take: 5,
          skip: 0,
          statuses: [
            BriefStatusEnum.Public,
            BriefStatusEnum.Private,
            BriefStatusEnum.Complete,
            BriefStatusEnum.Draft,
          ],
        })
        .subscribe({
          next: (response) => {
            if (this.loadedBrandBriefsForAccountId !== businessAccountId) {
              return;
            }
            this.brandBriefs.set((response?.data ?? []).slice(0, 5));
            this.brandBriefsLoading.set('loaded');
            this.cdr.markForCheck();
          },
          error: () => {
            if (this.loadedBrandBriefsForAccountId !== businessAccountId) {
              return;
            }
            this.brandBriefs.set([]);
            this.brandBriefsLoading.set('error');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  private clearHiredBriefs(): void {
    this.hiredBriefs.set([]);
    this.hiredBriefsLoading.set('idle');
    this.hiredBriefsExpanded.set(false);
    this.appliedBriefs.set([]);
    this.appliedBriefsLoading.set('idle');
    this.appliedBriefsExpanded.set(false);
    this.loadedCreatorBriefsForUserId = null;
  }

  private clearBrandBriefs(): void {
    this.brandBriefs.set([]);
    this.brandBriefsLoading.set('idle');
    this.loadedBrandBriefsForAccountId = null;
  }

  loadAdmins(): void {
    this.subscriptionList.add(
      this.supportService.listAssignableAdmins().subscribe({
        next: (admins) => {
          this.admins = admins;
          this.cdr.markForCheck();
        },
      }),
    );
  }

  loadTemplateMessages(): void {
    this.subscriptionList.add(
      this.supportService.getSupportSettings().subscribe({
        next: (settings) => {
          this.templateMessages = settings.templateMessages ?? [];
          const delayMinutes =
            typeof settings.adminReplyNotifyDelayMinutes === 'number'
              ? settings.adminReplyNotifyDelayMinutes
              : 2;
          this.notifyDelayMs.set(Math.max(0, delayMinutes) * 60 * 1000);
          this.skipDelayForFirstResponse.set(settings.skipDelayForFirstResponse !== false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.templateMessages = [];
          this.cdr.markForCheck();
        },
      }),
    );
  }

  onAttachmentsChange(attachments: SupportAttachment[]): void {
    this.attachments = attachments;
  }

  onEditingAttachmentsChange(attachments: SupportAttachment[]): void {
    this.editingAttachments = attachments;
  }

  insertTemplate(template: SupportTemplateMessage): void {
    const text = this.resolveTemplateBody(template);
    this.reply = this.reply.trim() ? `${this.reply.trimEnd()}\n\n${text}` : text;
    this.error.set('');
    this.closeTemplatesMenu();
  }

  sendTemplate(template: SupportTemplateMessage): void {
    if (this.submitting()) {
      return;
    }

    const body = this.resolveTemplateBody(template).trim();
    if (!body) {
      return;
    }

    this.closeTemplatesMenu();
    this.submitting.set(true);
    this.error.set('');
    this.recordAiFeedback(body);
    this.subscriptionList.add(
      this.supportService.addAdminMessage(this.ticketId, body).subscribe({
        next: (ticket: SupportTicketDetail) => {
          this.ticket.set(ticket);
          this.reply = '';
          this.attachments = [];
          this.submitting.set(false);
          this.queueScrollToLatest('smooth');
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('Could not send reply.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private resolveTemplateBody(template: SupportTemplateMessage): string {
    return applySupportTemplateMessage(template.body, this.ticket()?.user?.firstName);
  }

  sendReply(): void {
    const body = this.reply.trim();
    if (!body && this.attachments.length === 0) {
      return;
    }

    this.submitting.set(true);
    this.error.set('');
    this.recordAiFeedback(body);
    const attachments = this.attachments.length ? this.attachments : undefined;
    this.subscriptionList.add(
      this.supportService.addAdminMessage(this.ticketId, body, attachments).subscribe({
        next: (ticket: SupportTicketDetail) => {
          this.ticket.set(ticket);
          this.reply = '';
          this.attachments = [];
          this.submitting.set(false);
          this.queueScrollToLatest('smooth');
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('Could not send reply.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  canManageMessage(message: SupportMessage): boolean {
    return message.authorType === 'admin' && message.authorUserId === this.currentAdminUserId;
  }

  startEdit(message: SupportMessage): void {
    this.editingMessageId = message.id;
    this.editingBody = message.body;
    this.editingAttachments = [...(message.attachments ?? [])];
    this.error.set('');
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editingBody = '';
    this.editingAttachments = [];
  }

  canSaveEdit(): boolean {
    return !!this.editingBody.trim() || this.editingAttachments.length > 0;
  }

  saveEdit(messageId: string): void {
    const body = this.editingBody.trim();
    if (!body && this.editingAttachments.length === 0) {
      return;
    }

    this.submitting.set(true);
    this.error.set('');
    const attachments = this.editingAttachments.length ? this.editingAttachments : undefined;
    this.subscriptionList.add(
      this.supportService
        .updateAdminMessage(this.ticketId, messageId, body, attachments)
        .subscribe({
          next: (ticket: SupportTicketDetail) => {
            this.ticket.set(ticket);
            this.cancelEdit();
            this.submitting.set(false);
            this.cdr.markForCheck();
          },
          error: () => {
            this.submitting.set(false);
            this.error.set('Could not update message.');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  deleteMessage(message: SupportMessage): void {
    if (!confirm('Delete this message? This cannot be undone.')) {
      return;
    }

    this.submitting.set(true);
    this.error.set('');
    this.subscriptionList.add(
      this.supportService.deleteAdminMessage(this.ticketId, message.id).subscribe({
        next: (ticket: SupportTicketDetail) => {
          this.ticket.set(ticket);
          if (this.editingMessageId === message.id) {
            this.cancelEdit();
          }
          this.submitting.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('Could not delete message.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  assignAdmin(): void {
    if (!this.assignmentChanged()) {
      return;
    }

    this.subscriptionList.add(
      this.supportService.assignTicket(this.ticketId, this.selectedAdminId).subscribe({
        next: (ticket: SupportTicketDetail) => {
          this.ticket.set(ticket);
          this.selectedAdminId = ticket.assignedAdminUserId;
          this.supportService.refreshAssignedOpenTicketCount();
          this.cdr.markForCheck();
        },
      }),
    );
  }

  assignmentChanged(): boolean {
    return this.selectedAdminId !== (this.ticket()?.assignedAdminUserId ?? null);
  }

  onSelectedAdminChange(): void {
    this.cdr.markForCheck();
  }

  closeTicket(): void {
    this.submitting.set(true);
    this.subscriptionList.add(
      this.supportService.closeAdminTicket(this.ticketId).subscribe({
        next: (ticket: SupportTicketDetail) => {
          this.ticket.set(ticket);
          this.submitting.set(false);
          this.supportService.refreshAssignedOpenTicketCount();
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('Could not close ticket.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  goBack(): void {
    this.router.navigate(['/support-admin']);
  }

  get previousTicketId(): string | null {
    const index = this.ticketOrder.indexOf(this.ticketId);
    if (index <= 0) {
      return null;
    }
    return this.ticketOrder[index - 1];
  }

  get nextTicketId(): string | null {
    const index = this.ticketOrder.indexOf(this.ticketId);
    if (index === -1 || index >= this.ticketOrder.length - 1) {
      return null;
    }
    return this.ticketOrder[index + 1];
  }

  goToPreviousTicket(): void {
    if (this.previousTicketId) {
      this.navigateToTicket(this.previousTicketId);
    }
  }

  goToNextTicket(): void {
    if (this.nextTicketId) {
      this.navigateToTicket(this.nextTicketId);
    }
  }

  private navigateToTicket(ticketId: string): void {
    this.router.navigate(['/support-admin', ticketId], {
      state: { ticketIds: this.ticketOrder },
    });
  }

  canSendReply(): boolean {
    return !!this.reply.trim() || this.attachments.length > 0;
  }

  private shouldRequestAiDraft(ticket: SupportTicketDetail): boolean {
    if (ticket.status !== 'open') {
      return false;
    }
    const lastMessage = ticket.messages?.[ticket.messages.length - 1];
    return lastMessage?.authorType === 'user';
  }

  private maybeRequestAiDraft(ticket: SupportTicketDetail): void {
    if (!this.shouldRequestAiDraft(ticket)) {
      return;
    }
    this.requestAiDraft(false);
  }

  requestAiDraft(refresh = true): void {
    if (!this.ticketId || this.aiLoading()) {
      return;
    }

    this.aiLoading.set(true);
    this.aiError.set('');
    this.subscriptionList.add(
      this.supportService.createAdminAiDraft(this.ticketId, refresh).subscribe({
        next: (suggestion) => {
          this.aiSuggestion.set(suggestion);
          this.aiLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.aiLoading.set(false);
          this.aiError.set('Could not suggest a reply.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  useAiDraft(): void {
    const draft = this.aiSuggestion()?.draftBody?.trim();
    if (!draft) {
      return;
    }
    this.reply = this.reply.trim() ? `${this.reply.trimEnd()}\n\n${draft}` : draft;
    this.error.set('');
    this.cdr.markForCheck();
  }

  outboundTicketRecipient(action: SupportAiAction): string {
    return (
      action.payload.brandName ||
      action.payload.contactNames?.[0] ||
      action.payload.contactEmails?.[0] ||
      'brand'
    );
  }

  canSendOutboundTicket(action: SupportAiAction): boolean {
    return !!action.payload.targetUserId?.trim() && !!action.payload.message?.trim();
  }

  approveAiAction(action: SupportAiAction): void {
    if (this.approvingActionId() || action.approvedAt) {
      return;
    }

    this.approvingActionId.set(action.id);
    this.aiError.set('');
    this.subscriptionList.add(
      this.supportService.approveAdminAiAction(this.ticketId, action.id).subscribe({
        next: (suggestion) => {
          this.aiSuggestion.set(suggestion);
          this.approvingActionId.set(null);
          if (action.type === 'close_ticket') {
            this.loadTicket({ silent: true });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.approvingActionId.set(null);
          this.aiError.set(
            action.type === 'nudge_brand'
              ? 'Could not create the outbound ticket.'
              : action.type === 'notify_dev'
                ? 'Could not notify the dev team.'
                : 'Could not close the ticket.',
          );
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private recordAiFeedback(sentBody: string): void {
    const suggestion = this.aiSuggestion();
    if (!suggestion?.id || !sentBody.trim()) {
      return;
    }
    this.subscriptionList.add(
      this.supportService
        .recordAdminAiFeedback(this.ticketId, sentBody, suggestion.id)
        .subscribe({ error: () => undefined }),
    );
  }

  formatAlertPreferences(preferences?: SupportAlertPreferences): string {
    if (!preferences) {
      return '—';
    }

    const labels: string[] = [];
    if (preferences.sms) labels.push('SMS');
    if (preferences.whatsapp) labels.push('WhatsApp');
    if (preferences.email) labels.push('Email');
    if (preferences.push) labels.push('Push');
    return labels.length ? labels.join(', ') : '—';
  }

  formatUserType(userType?: SupportUserType | null): string {
    switch (userType) {
      case 'creator':
        return 'Creator';
      case 'brand':
        return 'Brand';
      case 'agency':
        return 'Agency';
      default:
        return '—';
    }
  }

  formatAppVersion(appVersion?: string | null, appOs?: string | null): string {
    const version = appVersion?.trim();
    const os = appOs?.trim();
    if (version && os) {
      return `${version} · ${os}`;
    }
    return version || os || '—';
  }

  formatSocialHandle(handle?: string | null): string {
    const trimmed = handle?.trim();
    if (!trimmed) {
      return '—';
    }
    return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
  }

  formatSocialProfile(handle?: string | null, followers?: number | null): string {
    const formattedHandle = this.formatSocialHandle(handle);
    if (formattedHandle === '—') {
      return '—';
    }
    if (followers == null || !Number.isFinite(followers) || followers <= 0) {
      return formattedHandle;
    }
    return `${formattedHandle} · ${this.formatService.convertIntToString(followers)}`;
  }

  formatCreatorGender(gender?: number | null): string {
    switch (gender) {
      case 1:
        return 'Male';
      case 2:
        return 'Female';
      case 3:
        return 'Non-binary';
      default:
        return '—';
    }
  }

  categoryLabel(): string {
    return supportTicketCategoryLabel(this.ticket()?.category);
  }

  openDurationLabel(): string {
    const current = this.ticket();
    if (!current?.createdDate) {
      return '—';
    }
    const startMs = Date.parse(current.createdDate);
    if (!Number.isFinite(startMs)) {
      return '—';
    }

    const endMs =
      current.status === 'closed' && current.closedAt
        ? Date.parse(current.closedAt)
        : this.nowMs();
    if (!Number.isFinite(endMs)) {
      return '—';
    }

    return this.formatDuration(Math.max(0, endMs - startMs));
  }

  firstResponseDurationLabel(): string {
    const current = this.ticket();
    if (!current?.createdDate || !current.firstAdminResponseAt) {
      return '—';
    }
    const startMs = Date.parse(current.createdDate);
    const responseMs = Date.parse(current.firstAdminResponseAt);
    if (!Number.isFinite(startMs) || !Number.isFinite(responseMs)) {
      return '—';
    }
    return this.formatDuration(Math.max(0, responseMs - startMs));
  }

  reopenCountLabel(): string {
    return String(this.ticket()?.reopenCount ?? 0);
  }

  notificationPills(message: SupportMessage): { label: string; className: string }[] {
    return this.channelPills(message.notificationsSent);
  }

  /**
   * Pending notify UI only while the latest message is still that admin reply and its
   * notify window (10s for a ticket's first response, otherwise the configured
   * adminReplyNotifyDelayMinutes) is open - extended by a short flash for a first response
   * so "Sent!" actually gets a moment on screen. A user reply cancels outbound alerts, so
   * the loader must clear immediately.
   */
  isNotificationPending(message: SupportMessage): boolean {
    if (message.authorType !== 'admin' || message.notificationsSent) {
      return false;
    }
    if (!this.hasAlertPreference(this.ticket()?.alertPreferences)) {
      return false;
    }
    if (!this.isLatestMessage(message.id)) {
      return false;
    }

    const createdMs = Date.parse(message.createdDate);
    if (!Number.isFinite(createdMs)) {
      return false;
    }

    const window =
      this.notifyDelayMsFor(message) +
      (this.isFirstResponseMessage(message) ? this.firstResponseSentFlashMs : 0);
    return this.nowMs() - createdMs < window;
  }

  pendingNotificationLabel(message: SupportMessage): string {
    if (this.isFirstResponseMessage(message)) {
      const secondsRemaining = this.firstResponseCountdownSeconds(message);
      return secondsRemaining > 0
        ? `First response - sending notification in ${secondsRemaining}`
        : 'Sent!';
    }

    const labels = this.channelPills(this.ticket()?.alertPreferences).map((pill) => pill.label);
    if (!labels.length) {
      return 'Sending';
    }
    return `Sending ${labels.join(', ')}`;
  }

  notificationProgressPercent(message: SupportMessage): number {
    const createdMs = Date.parse(message.createdDate);
    if (!Number.isFinite(createdMs)) {
      return 0;
    }
    const delay = this.notifyDelayMsFor(message);
    if (delay <= 0) {
      return 100;
    }
    const elapsed = Math.max(0, this.nowMs() - createdMs);
    return Math.min(100, (elapsed / delay) * 100);
  }

  /** True for the earliest admin-authored message in the thread - mirrors the backend's
   * "first admin response" check (ticket.firstAdminResponseAt), which gets the short
   * firstResponseNotifyDelayMs window instead of the full adminReplyNotifyDelayMinutes. */
  private isFirstAdminMessage(message: SupportMessage): boolean {
    const firstAdminMessage = (this.ticket()?.messages ?? []).find(
      (m) => m.authorType === 'admin',
    );
    return !!firstAdminMessage && firstAdminMessage.id === message.id;
  }

  /** Whether this message actually gets the short first-response countdown treatment - i.e.
   * it's the ticket's first admin message *and* an admin hasn't turned that behaviour off. */
  private isFirstResponseMessage(message: SupportMessage): boolean {
    return this.skipDelayForFirstResponse() && this.isFirstAdminMessage(message);
  }

  /** Whole seconds left until a first-response message's notification goes out - drives the
   * "sending notification in 10, 9, 8…" countdown in pendingNotificationLabel(). */
  private firstResponseCountdownSeconds(message: SupportMessage): number {
    const createdMs = Date.parse(message.createdDate);
    if (!Number.isFinite(createdMs)) {
      return 0;
    }
    const remainingMs = this.firstResponseNotifyDelayMs - (this.nowMs() - createdMs);
    return Math.max(0, Math.ceil(remainingMs / 1000));
  }

  private notifyDelayMsFor(message: SupportMessage): number {
    if (this.isFirstResponseMessage(message)) {
      return this.firstResponseNotifyDelayMs;
    }
    return this.notifyDelayMs();
  }

  messageCardClass(authorType: string): string {
    return authorType === 'admin'
      ? 'border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950/40'
      : 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900';
  }

  messageAuthorClass(authorType: string): string {
    return authorType === 'admin'
      ? 'text-indigo-700 dark:text-indigo-300'
      : 'text-secondary';
  }

  messageAuthorLabel(message: SupportMessage): string {
    if (message.authorType !== 'admin') {
      return 'User';
    }

    const adminName = this.resolveAdminDisplayName(message.authorUserId);
    return adminName ? `Support team (${adminName})` : 'Support team';
  }

  private resolveAdminDisplayName(authorUserId: string): string | null {
    if (!authorUserId) {
      return null;
    }

    const fromAdmins = this.admins.find((admin) => admin.userId === authorUserId);
    if (fromAdmins) {
      const name = `${fromAdmins.firstName} ${fromAdmins.lastName}`.trim();
      return name || fromAdmins.email || null;
    }

    const assignedAdmin = this.ticket()?.assignedAdmin;
    if (assignedAdmin?.userId === authorUserId) {
      return assignedAdmin.fullName?.trim() || null;
    }

    if (authorUserId === this.currentAdminUserId) {
      const shoutUser = this.currentUserService.shoutUser;
      const name = `${shoutUser?.firstName ?? ''} ${shoutUser?.lastName ?? ''}`.trim();
      return name || null;
    }

    return null;
  }

  statusClass(status: string): string {
    return status === 'open'
      ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300';
  }

  private isLatestMessage(messageId: string): boolean {
    const messages = this.ticket()?.messages ?? [];
    if (!messages.length) {
      return false;
    }
    return messages[messages.length - 1]?.id === messageId;
  }

  private hasAlertPreference(preferences?: SupportAlertPreferences | null): boolean {
    return (
      !!preferences &&
      (preferences.sms || preferences.whatsapp || preferences.email || preferences.push)
    );
  }

  private channelPills(
    preferences?: SupportAlertPreferences | null,
    classNames: { sms: string; whatsapp: string; email: string; push: string } = {
      sms: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      whatsapp: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      email: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      push: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    },
  ): { label: string; className: string }[] {
    if (!preferences) {
      return [];
    }

    const pills: { label: string; className: string }[] = [];
    if (preferences.sms) {
      pills.push({ label: 'SMS', className: classNames.sms });
    }
    if (preferences.whatsapp) {
      pills.push({ label: 'WhatsApp', className: classNames.whatsapp });
    }
    if (preferences.email) {
      pills.push({ label: 'Email', className: classNames.email });
    }
    if (preferences.push) {
      pills.push({ label: 'Push', className: classNames.push });
    }
    return pills;
  }
}
